import type { ReactElement } from 'react';
import { Container, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ButtonGroup, PrimaryButton } from '@noah-labs/fe-shared-ui-components';
import { webLinks } from '@noah-labs/shared-constants';
import type { MarkdownRemarkFrontmatterDeveloperCards, Maybe } from '../../../generated/types';
import { InquiryButton } from '../../components/buttons/InquiryButton';
import { TextWithNoahLogo } from '../../components/typography/TextWithNoahLogo';
import { HomepageCardWithImage } from './HomepageCardWithImage';

type PpDevelopersSection = {
  buttonLabel1: Maybe<string> | undefined;
  buttonLabel2: Maybe<string> | undefined;
  cards: MarkdownRemarkFrontmatterDeveloperCards[];
  image: Maybe<string> | undefined;
  subtitle: Maybe<string> | undefined;
  textWithIconLabel: Maybe<string> | undefined;
  title: Maybe<string> | undefined;
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginBottom: theme.spacing(12),
  overflow: 'hidden',
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 3),
  },
}));

export const StyledSection = styled('section')<{ backgroundImage: string | null | undefined }>(
  ({ backgroundImage, theme }) => ({
    backgroundColor: theme.palette.websiteBrand.greyscale.plainBlack,
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
    backgroundRepeat: 'no-repeat',
    width: '100%',
  }),
);

export function DevelopersSection({
  buttonLabel1,
  buttonLabel2,
  cards,
  image,
  subtitle,
  textWithIconLabel,
  title,
}: PpDevelopersSection): ReactElement {
  return (
    <StyledSection backgroundImage={image}>
      <StyledContainer disableGutters fixed>
        <Grid container>
          <Grid offset="auto" size={{ lg: 6 }}>
            <TextWithNoahLogo typographyColor="text.white">{textWithIconLabel}</TextWithNoahLogo>
            <Typography color="text.white" variant="headingL">
              {title}
            </Typography>
            <Stack spacing={3}>
              <Typography color="text.white">{subtitle}</Typography>
              {cards.map((card) => (
                <HomepageCardWithImage {...card} key={card.id} directionFrom="right" />
              ))}
              <ButtonGroup direction="row">
                <PrimaryButton
                  color="primaryBrand"
                  href={webLinks.docs.path}
                  target="_blank"
                  variant="outlined"
                >
                  {buttonLabel1}
                </PrimaryButton>
                <InquiryButton color="info" variant="outlined">
                  {buttonLabel2}
                </InquiryButton>
              </ButtonGroup>
            </Stack>
          </Grid>
        </Grid>
      </StyledContainer>
    </StyledSection>
  );
}
